/**
 * The selectable half of a scope-picker row: the region's name, and its
 * director on the right (BON-534).
 *
 * Matches `region-selector.jsx` — the design puts the count on the all-regions
 * row only, and a region row carries its director alone.
 *
 * Built to survive more hierarchy than we have: the name truncates rather than
 * pushing the director off the row, and indentation stops deepening past
 * `MAX_INDENT_DEPTH` so a fourth or fifth level still leaves room to read.
 *
 * A sibling of the expand caret, never nested inside it.
 */
import { Link } from 'react-router-dom';

import type { ScopeRowVariant } from './scope-row-metrics';
import { SCOPE_ROW_METRICS } from './scope-row-metrics';
import type { ScopeHrefFor, ScopeRegion } from './scope.types';

export interface ScopeMenuLinkProps {
  row: ScopeRegion;
  isHere: boolean;
  hrefFor: ScopeHrefFor;
  onPick: () => void;
  variant: ScopeRowVariant;
}

export function ScopeMenuLink({ row, isHere, hrefFor, onPick, variant }: ScopeMenuLinkProps) {
  const size = SCOPE_ROW_METRICS[variant];
  return (
    <Link
      to={hrefFor(row)}
      role="menuitem"
      // Only when true: `aria-current="false"` still counts as present, which
      // would mark every row as the current one.
      {...(isHere ? { 'aria-current': true as const } : {})}
      onClick={onPick}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        minHeight: size.rowHeight,
        padding: size.rowPadding,
        borderRadius: size.radius,
        textDecoration: 'none',
        color: isHere ? 'var(--nb-primary-2)' : 'var(--nb-ink)',
      }}
    >
      <span
        style={{
          fontSize: row.depth > 0 ? size.childLabel : size.label,
          flex: '1 1 auto',
          fontWeight: isHere ? 700 : row.depth > 0 ? 500 : 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.name}
      </span>
      <span
        style={{
          flex: '0 1 auto',
          fontSize: size.meta,
          color: 'var(--nb-subtle)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.directorName ?? ''}
      </span>
    </Link>
  );
}
